'use client';

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { hexToRgbChannels } from '@/lib/utils';
import type { Branding } from '@/types';

/**
 * Fonte unica da identidade visual no lado do cliente.
 * Nenhum componente escreve o nome ou as cores da marca: tudo vem daqui,
 * e as cores viram variaveis CSS aplicadas na raiz do documento.
 */

const BrandingContext = createContext<Branding | null>(null);

export function BrandingProvider({
  branding,
  children,
}: {
  branding: Branding;
  children: ReactNode;
}) {
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', hexToRgbChannels(branding.colors.primary));
    root.style.setProperty('--brand-secondary', hexToRgbChannels(branding.colors.secondary));
    root.style.setProperty('--brand-accent', hexToRgbChannels(branding.colors.accent));
    root.style.setProperty('--brand-sidebar', hexToRgbChannels(branding.colors.sidebar));
    root.style.setProperty('--brand-background', hexToRgbChannels(branding.colors.background));
  }, [branding.colors]);

  const value = useMemo(() => branding, [branding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): Branding {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding precisa estar dentro de BrandingProvider.');
  }
  return context;
}
